import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, BarChart3, Megaphone, Users, Phone, PhoneIncoming, Bot, Plug, Settings, Mic, LifeBuoy, CreditCard, Sparkles, Workflow } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/pk-logo.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, id: "campaigns" },
  { to: "/leads", label: "Leads", icon: Users, id: "leads" },
  { to: "/logs", label: "Call Logs", icon: Phone, id: "logs" },
  { to: "/inbound", label: "Inbound", icon: PhoneIncoming, id: "inbound", badge: true },
  { to: "/agent", label: "AI Agent", icon: Bot, id: "agent" },
  { to: "/workflows", label: "Workflows", icon: Workflow, id: "workflows" },
  { to: "/integrations", label: "Integrations", icon: Plug, id: "integrations" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/support", label: "Support", icon: LifeBuoy, id: "support" },
  { to: "/whats-new", label: "What's New", icon: Sparkles, id: "whats-new" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

export default function Sidebar({ open, onClose }) {
  const { user } = useAuth();
  const [missed, setMissed] = useState(0);
  const location = useLocation();

  useEffect(() => {
    api.get("/inbound/missed").then((r) => setMissed(r.data.length)).catch(() => {});
  }, [location.pathname]);

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={onClose} />}
      <aside className={`fixed lg:static z-40 h-screen w-64 bg-sidebar text-slate-100 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <img src={logo} alt="Parth Kalyani" className="h-11 w-auto" data-testid="app-logo" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onClose}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-sidebar-active text-white" : "text-slate-300 hover:bg-sidebar-hover hover:text-white"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              <span className="flex-1">{n.label}</span>
              {n.badge && missed > 0 && (
                <span className="text-[10px] font-bold bg-purple-500 text-white rounded-full px-1.5 py-0.5" data-testid="inbound-badge">{missed}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
            {(user?.name || "A").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.company_name || user?.name || "Client"}</div>
            <div className="text-[11px] text-indigo-300">{user?.plan ? `${user.plan} Plan` : "Client"}</div>
          </div>
        </div>
      </aside>
    </>
  );
}
